<!-- @format -->
<template>
    <div class="collateral-inject-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ steps[step].title || 'Marketing Collaterals' }}</h3>
                <div class="widget-header-actions">
                    <div class="svg-container widget-close-button" @click="closeWidget">
                        <widget-close-svg />
                    </div>
                </div>
            </div>
            <div class="content-wrapper">
                <div v-show="step == 'library'">
                    <collateral-display
                        :account-id="accountId"
                        :client-id="clientId"
                        :agency-id="agencyId"
                        :selectable="false"
                        filterable
                        multiview
                        selector
                        multiselector
                        searchable
                        previewable
                        @select="select"
                        @update:selectedItems="updateSelectedCollateral($event)"
                    />
                </div>
                <div v-show="step == 'Attachments'" class="attachment-options-container">
                    <div class="special-card">
                        <h4 class="heading">How would you like to send your file?</h4>
                        <p v-if="areSelectedItemsEmpty" class="red-accent attachment-warning">
                            *No attachments were selected.
                        </p>
                        <div
                            v-if="injectOptions.includes('Link')"
                            class="radio-btn-container"
                            @click="setAttachmentType('Link')"
                        >
                            <div class="radio-btn">
                                <div
                                    class="selected-dot"
                                    :class="{ selected: attachmentType == 'Link' }"
                                ></div>
                            </div>
                            <label
                                >Insert as a link (Recommended)
                                <app-info-icon :info="info" icon="solid"
                            /></label>
                        </div>

                        <div v-if="injectOptions.includes('Inline')" class="radio-btn-container">
                            <div class="radio-btn" @click="setAttachmentType('Inline')">
                                <div
                                    class="selected-dot"
                                    :class="{ selected: attachmentType == 'Inline' }"
                                ></div>
                            </div>
                            <label>Inline <span class="accent-color">(Only images)</span></label>
                        </div>

                        <div
                            v-if="injectOptions.includes('Attachment')"
                            class="radio-btn-container"
                        >
                            <div class="radio-btn" @click="setAttachmentType('Attachment')">
                                <div
                                    class="selected-dot"
                                    :class="{ selected: attachmentType == 'Attachment' }"
                                ></div>
                            </div>
                            <label>Attachment</label>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" :on-click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <button
                        v-if="step == 'Attachments'"
                        class="transparent-btn btn normal"
                        @click="goToSelectCollateral"
                    >
                        Back
                    </button>
                    <app-button :on-click="steps[step].action" :loading="steps[step].loading">
                        {{ steps[step].buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const CollateralDisplay = () => import('@/components/collateral/collateral-display');
const AppInfoIcon = () => import('@/components/common/app-info-icon');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'collateral-inject-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'app-info-icon': AppInfoIcon,
        'collateral-display': CollateralDisplay,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            info:
                "<div class='content'><h3>Insert as a Link</h3><p>There are several reasons why you would want to add collateral as a link.<ul><li>Email providers may view links as safer than attachments.</li><li>Attachments can be limited by their size.</li><li>Links can be tracked so you know when your contact has clicked through.</li></ul></p></div>",
            isInitializing: true,
            selectedItems: [],
            step: null,
            attachmentType: 'Link',
            steps: {
                library: {
                    title: 'Marketing Collateral',
                    buttonText: 'Select',
                    action: this.goToAttachmentStyleStep,
                    loading: false,
                },

                Attachments: {
                    title: 'Attachments',
                    buttonText: 'Add',
                    action: this.emitSelectedItems,
                    loading: false,
                },
            },
        };
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },

        clientId() {
            return this.$store.state.context.client.id;
        },

        agencyId() {
            return this.$store.state.context.agency.id;
        },

        areSelectedItemsEmpty() {
            return this.selectedItems.length == 0;
        },

        areAllSelectedItemsLinks() {
            var areAllSelectedItemsLinks = false;

            var linkCollaterals = this.selectedItems.filter((item) => {
                return item.fileType.toUpperCase() == 'LINK';
            });

            if (this.selectedItems.length == linkCollaterals.length) {
                areAllSelectedItemsLinks = true;
            }

            return areAllSelectedItemsLinks;
        },

        injectOptions() {
            return this.widget.injectOptions || ['Link', 'Attachment', 'Inline'];
        },
    },

    created() {
        this.attachmentType = this.injectOptions[0];
        this.step = 'library';
    },

    methods: {
        emitSelectedItems() {
            var payload = {
                selectedItems: this.selectedItems,
                attachmentType: this.attachmentType,
            };

            EventBus.$emit('library-payload-emitted', payload);
            this.closeWidget();
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        setParentFocus(event) {
            event.target.parentElement.classList.add('active');
        },

        removeParentFocus(event) {
            event.target.parentElement.classList.remove('active');
        },

        goToAttachmentStyleStep() {
            if (this.areAllSelectedItemsLinks) {
                this.bypassAttachmentStep();
            } else {
                this.step = 'Attachments';
            }
        },

        setAttachmentType(type) {
            this.attachmentType = type;
        },

        goToSelectCollateral() {
            this.step = 'library';
        },

        bypassAttachmentStep() {
            this.setAttachmentType('WebPage');
            this.emitSelectedItems();
        },

        select(item) {
            this.selectedItem = item;
        },

        updateSelectedCollateral(selectedCollateral) {
            this.selectedItems = selectedCollateral;
        },
    },
};
</script>

<style>
.collateral-inject-widget .master-cards .select-sort-options {
    display: none;
}

.collateral-inject-widget {
    width: 100%;
}

.collateral-inject-widget .widget-header-actions {
    display: flex;
}

.collateral-inject-widget .action-container {
    display: flex;
}

.collateral-inject-widget .action-container .btn {
    margin-left: 15px;
}

.collateral-inject-widget .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.collateral-inject-widget .action-container .btn.transparent-btn {
    background: transparent;
    color: #18aeff;
}

.collateral-inject-widget .action-container .btn.transparent-btn:hover {
    background: #dbedfa;
}

.collateral-inject-widget .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.collateral-inject-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}

.collateral-inject-widget .radio-btn-container {
    display: flex;
    align-items: center;
    margin: 0 0 10px;
}

.collateral-inject-widget .radio-btn-container .radio-btn {
    width: 30px;
    height: 30px;
    border: 2px solid #382153;
}

.collateral-inject-widget .radio-btn-container .radio-btn .selected-dot {
    width: 18px;
    height: 18px;
    top: 4px;
    left: 4px;
}

.collateral-inject-widget .radio-btn-container label {
    font-size: 16px;
    font-weight: 400;
    margin-bottom: 0;
}

.collateral-inject-widget .radio-btn:hover {
    cursor: pointer;
}

.collateral-inject-widget .attachment-options-container {
    padding: 25px;
}

.collateral-inject-widget .attachment-options-container h4 {
    margin-bottom: 20px;
}

.collateral-inject-widget .radio-btn .selected-dot.selected {
    display: block;
}

.collateral-inject-widget .action-container .btn.normal {
    height: 40px;
    border-radius: 20px;
    padding: 0px 25px;
    font-size: 14px;
    line-height: 17px;
}

.collateral-inject-widget .action-container .btn.normal:hover {
    transition: background-color 250ms ease-in;
}

.collateral-inject-widget .attachment-warning {
    font-weight: 600;
    margin-bottom: 15px;
}

.collateral-inject-widget .special-card {
    position: relative;
    margin-top: 20px;
    padding: 35px 15px 15px 15px;
    border: solid 2px #e9e9ef;
    border-radius: 15px;
}

.collateral-inject-widget .special-card .heading {
    position: absolute;
    top: -22px;
    left: calc(50% - 173px);
    padding: 10px 15px;
    background-color: #fff;
}

.collateral-inject-widget .accent-color {
    color: #877d88;
}

.collateral-inject-widget .library-table {
    margin-top: 20px;
}

.collateral-inject-widget .collateral-display .collateral-display-item .item-wrapper {
    height: 60%;
}

.collateral-inject-widget .collateral-display .master-cards .master-card-wrapper {
    padding: 0;
    display: flex;
    justify-content: space-between;
}

.collateral-inject-widget .collateral-display .collateral-display-item {
    max-width: 45%;
}

.collateral-inject-widget .app-info-icon .icon-container .grey-info-icon-svg ellipse,
.collateral-inject-widget .app-info-icon .icon-container .grey-info-icon-svg text {
    stroke: #877d88;
    fill: #877d88;
}

.collateral-inject-widget .content-wrapper .master-card-controls .custom-filter-container {
    right: -290px;
    width: 560px;
}
</style>
